import React, { createContext, useContext, useEffect, useState } from "react";
import { useSse } from "../hooks/useSse";
import { SSE_REGISTER_URL } from "../api/EventApi";
import {
  DashboardStatusDTO,
  ReviewPatchSseDto,
  ReviewPlayInfo,
  ReviewRefreshDto,
  ReviewTimingCalcDTO,
  ReviewTimingNotes,
} from "../types/ReviewDetailsTypes";
import { SlackAlertType } from "../types/SlackAlertTypes";
import {
  GameAdditionalSsmo,
  GameAnglesNotShown,
  GameCameraInput,
  GameHfrSsmo,
  GameSetting,
  GameTech,
} from "../types/CamTypes";
import { CoachContactType } from "../types/CoachContactTypes";
import { ReplayOfficialAssignmentByGame } from "../types/DailyAssignmentTypes";
import { GameCheck } from "../types/CheckTypes";
import { EquipmentRequestType } from "../types/EquipmentRequestTypes";
import { IssueLogType, IssueLogMiscNotes } from "../types/IssueLogTypes";
import { SpecialLink, SpecialLinkAngle } from "../types/SpecialLinkTypes";
import { GameStatusUpdateDto } from "../types/GameDtoTypes";

export enum NotificationAction {
  AddDraft = "ADD_DRAFT",
  Delete = "DELETE",
  Insert = "INSERT",
  Update = "UPDATE",
  UpdateDraft = "UPDATE_DRAFT",
}

export type NotificationPayload = {
  action?: string;
  lastUpdated?: string;
  updatedUserEmail?: string;
  updatedUserName?: string;
};

// TODO: Separate Insert/Update notifications
type NotificationOptions = {
  slackAlert?: NotificationPayload & {
    data?: SlackAlertType;
  };
  coachContactLog?: NotificationPayload & {
    data?: CoachContactType;
  };
  coachContactLogDelete?: NotificationPayload & {
    data?: number;
  };
  equipmentRequest?: NotificationPayload & {
    data?: EquipmentRequestType;
  };
  equipmentRequestDelete?: NotificationPayload & {
    data?: number;
  };
  gameAdditionalSsmo?: NotificationPayload & {
    data?: GameAdditionalSsmo;
  };
  gameAnglesNotShown?: NotificationPayload & {
    data?: GameAnglesNotShown;
  };
  gameCameraInput?: NotificationPayload & {
    data?: GameCameraInput;
  };
  gameSetting?: NotificationPayload & {
    data?: GameSetting;
  };
  replayOfficialAssignmentByGame?: NotificationPayload & {
    data?: ReplayOfficialAssignmentByGame;
  };
  gameCheck?: NotificationPayload & {
    data?: GameCheck;
  };
  gameCheckDelete?: NotificationPayload & {
    data?: number;
  };
  gameHfrSsmo?: NotificationPayload & {
    data?: GameHfrSsmo;
  };
  gameHfrSsmoOrder?: NotificationPayload & {
    data?: GameHfrSsmo[];
  };
  gameTech?: NotificationPayload & {
    data?: GameTech;
  };
  gameTechDelete?: NotificationPayload & {
    data?: number;
  };
  issueLog?: NotificationPayload & {
    data?: IssueLogType;
  };
  issueLogDelete?: NotificationPayload & {
    data?: number;
  };
  issueLogMiscNotes?: NotificationPayload & {
    data?: IssueLogMiscNotes;
  };
  issueLogMiscNotesDelete?: NotificationPayload & {
    data?: number;
  };
  refreshOperatorDashboard?: NotificationPayload & {
    data?: number;
  };
  refreshSupportDashboard?: NotificationPayload & {
    data?: number;
  };
  refreshRmDashboard?: NotificationPayload & {
    data?: number;
  };
  refreshFloaterDashboard?: NotificationPayload & {
    data?: number;
  };
  refreshTdDashboard?: NotificationPayload & {
    data?: number;
  };
  refreshAdminDashboard?: NotificationPayload & {
    data?: number;
  };
  rmReviewStatus?: NotificationPayload & {
    data?: DashboardStatusDTO;
  };
  reviewRefresh?: NotificationPayload & {
    data?: ReviewRefreshDto;
  };
  floaterReviewStatus?: NotificationPayload & {
    data?: DashboardStatusDTO;
  };
  slackAlertFloater?: NotificationPayload & {
    data?: SlackAlertType;
  };
  deleteReview?: NotificationPayload & {
    data?: number;
  };
  specialLink?: NotificationPayload & {
    data?: SpecialLink;
  };
  deleteSpecialLink?: NotificationPayload & {
    data?: number;
  };
  specialLinkAngle?: NotificationPayload & {
    data?: SpecialLinkAngle[];
  };
  reviewTimingUpdate?: NotificationPayload & {
    data?: ReviewPatchSseDto;
  };
  reviewTimingCalc?: NotificationPayload & {
    data?: ReviewTimingCalcDTO;
  };
  reviewRocStaffUpdate?: NotificationPayload & {
    data?: ReviewPatchSseDto;
  };
  reviewScoreBaseoutUpdate?: NotificationPayload & {
    data?: ReviewPatchSseDto;
  };
  reviewLinkUpdate?: NotificationPayload & {
    data?: ReviewPatchSseDto;
  };
  reviewTdDataUpdate?: NotificationPayload & {
    data?: ReviewPatchSseDto;
  };
  reviewInfoUpdate?: NotificationPayload & {
    data?: ReviewPatchSseDto;
  };
  reviewPlayInfoUpdate?: NotificationPayload & {
    data?: ReviewPatchSseDto;
  };
  reviewPlayInfoParentClear?: NotificationPayload & {
    data?: ReviewPlayInfo;
  };
  reviewTimingNotesSave?: NotificationPayload & {
    data?: ReviewTimingNotes;
  };
  reviewTimingNotesDelete?: NotificationPayload & {
    data?: ReviewTimingNotes;
  };
  multipleReviewUpdate?: NotificationPayload & {
    // data is just gamePK
    data?: number;
  };
  gameStatusUpdate?: NotificationPayload & {
    data?: GameStatusUpdateDto;
  };
};

type SseNotificationContext = {
  notification: NotificationOptions;
};

const SseNotificationContext = createContext<SseNotificationContext>({
  notification: null,
});

const SseNotificationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [notification, setNotification] = useState<NotificationOptions>({});
  const [data] = useSse(SSE_REGISTER_URL, "sseNotification");

  // the first useEffect triggers all dependent components to perform necessary re-renders
  // the second useEffect empties out the notification object to prevent unnecessary re-renders
  // the second useEffect will not "overwrite" necessary updates from the first useEffect

  useEffect(() => {
    if (data?.payload) {
      const entities = data.payload?.entities;
      if (!!entities) {
        Object.keys(entities).forEach(k => {
          entities[k].action = data.action;
        });
      }
      const newNotification = { ...notification, ...entities };
      setNotification(newNotification);
    }
  }, [data]);

  useEffect(() => {
    if (Object.keys(notification).length > 0) {
      setNotification({});
    }
  }, [notification]);

  return <SseNotificationContext.Provider value={{ notification }}>{children}</SseNotificationContext.Provider>;
};

const useSseNotification = (): SseNotificationContext => {
  const context = useContext<SseNotificationContext>(SseNotificationContext);
  if (context === undefined) {
    throw new Error(`useSseNotificationContext must be used within a SseNotificationProvider`);
  }
  return context;
};

export { SseNotificationContext, SseNotificationProvider, useSseNotification };
